import React, { useCallback, useState } from 'react';
import classnames from 'classnames';

import { Text, TextType } from 'ui-components/components/text';
import { PARAMETER } from '../../../../../constants/parameter-name-for-update';

import messages from './messages';
import style from './style.module.less';

const NAME_CONTACT_FIELD_ID = 'dangerous-goods-name-contact';
const PHONE_CONTACT_FIELD_ID = 'dangerous-goods-phone-contact';

/**
 * @typedef {object} InputValueProps
 * @property {string} parameter - The input parameter containing dangerous goods information in the format "name|phone".
 */

/**
 * @callback OnChangeCallback
 * @param {object} updatedValues - The updated values.
 * @param {string} updatedValues[PARAMETER] - The new concatenated "name|phone" value.
 */

/**
 * SetContainsDangerousGoods Component
 * @param {object} props
 * @param {InputValueProps} props.inputValueProps - Input values for dangerous goods information.
 * @param {OnChangeCallback} props.onChange - Callback function triggered when values change.
 * @returns {JSX.Element}
 */

const SetContainsDangerousGoods = ({ inputValueProps, onChange }) => {
  const { parameter } = inputValueProps;
  const valueParts = parameter?.toString().split('|') || [];
  const [nameContact, setNameContact] = useState(valueParts[0] || '');
  const [phoneContact, setPhoneContact] = useState(valueParts[1] || '');

  const updateValue = useCallback(
    ({ newNameContact, newPhoneContact }) => {
      const nameContactPart = newNameContact || nameContact;
      const phoneContactPart = newPhoneContact || phoneContact;

      onChange({ [PARAMETER]: `${nameContactPart}|${phoneContactPart}` });
    },
    [nameContact, phoneContact, onChange],
  );

  const onSetNameContact = useCallback(
    (e) => {
      setNameContact(e?.target?.value);
    },
    [setNameContact],
  );

  const onBlurNameContact = useCallback(() => {
    updateValue({ newNameContact: nameContact });
  }, [nameContact, updateValue]);

  const onSetPhoneContact = useCallback(
    (e) => {
      setPhoneContact(e?.target?.value);
    },
    [setPhoneContact],
  );

  const onBlurPhoneContact = useCallback(() => {
    updateValue({ newPhoneContact: phoneContact });
  }, [phoneContact, updateValue]);

  return (
    <div className={style['multi-control']}>
      <div className={style['multi-control-row']}>
        <div>
          <Text htmlFor={NAME_CONTACT_FIELD_ID} textType={TextType.Label} value={messages.setContainsDangerousGoodsNameContact} />
        </div>
        <div>
          <input
            className={style['text-input']}
            id={NAME_CONTACT_FIELD_ID}
            onBlur={onBlurNameContact}
            onChange={onSetNameContact}
            value={nameContact}
          />
        </div>
      </div>
      <div className={classnames(style.stacked, style['multi-control-row'])}>
        <div>
          <Text htmlFor={PHONE_CONTACT_FIELD_ID} textType={TextType.Label} value={messages.setContainsDangerousGoodsPhoneContact} />
        </div>
        <div>
          <input
            className={style['text-input']}
            id={PHONE_CONTACT_FIELD_ID}
            onBlur={onBlurPhoneContact}
            onChange={onSetPhoneContact}
            value={phoneContact}
          />
        </div>
      </div>
    </div>
  );
};

export default SetContainsDangerousGoods;
